// Per-organisation data profile (P7a — data-access enforcement).
//
// GET /api/data-access/me says which data profile the logged-in user's
// organisation has. Client 1 (Wellington Water) has a "full" profile and
// sees the app exactly as before. A public-only organisation, or one with
// only some of the grants, gets a "GIS-only view": the map with public
// layers only. The backend enforces this: every route that reads pipeline
// data returns 404 for such an org. This module only decides what to show,
// so those routes are never called at startup.
//
// Fail-safe: if the call fails, or an older backend leaves the P7a fields
// out, the profile is treated as full / not GIS-only, so client 1 is never
// broken by this module.
import { getJson, NotAvailableError } from "./api";
import { withRetry } from "./bootRetry";
import { getState } from "./state";
import type { CouncilNetwork } from "./councilNetworksCore";

export type ProfileKind = "full" | "public" | "partial";

export interface DataProfile {
  client_id: number | null;
  client_slug: string | null;
  client_name: string | null;
  profile_kind: ProfileKind;
  profile_label: string;
  gis_only: boolean;
  public_dataset: string | null;
  enforcing: boolean;
  granted: string[];
  not_granted: string[];
  /** Council public network this org is served (e.g. "KCDC"), 2026-10-07. */
  council_network: string | null;
  /** The council network's licence credit (e.g. "Contains data sourced from
   * Waimakariri District Council, licensed under CC BY 4.0"), shown on the map. */
  attribution: string | null;
  /** [west, south, east, north] WGS84 extent to open the map on. */
  home_bbox: [number, number, number, number] | null;
  /** Council networks shown ALONGSIDE the main map (2026-10-28; WISL:
   * Kapiti + Waimakariri next to Wellington). [] for most organisations. */
  council_networks: CouncilNetwork[];
}

interface DataAccessMeResponse {
  client_id?: number | null;
  client_slug?: string | null;
  client_name?: string | null;
  profile_kind?: ProfileKind;
  profile_label?: string;
  gis_only?: boolean;
  public_dataset?: string | null;
  enforcing?: boolean;
  granted?: unknown[];
  not_granted?: unknown[];
  council_network?: string | null;
  attribution?: string | null;
  home_bbox?: number[] | null;
  council_networks?: unknown[];
}

/** Dataset name shown when gis_only is set but public_dataset is missing. */
const DEFAULT_PUBLIC_DATASET = "public_network";

/** Shown in place of a panel whose data this profile may not read. */
export const NOT_AVAILABLE_TEXT = "Not available in your data profile.";

const FULL_FALLBACK: DataProfile = {
  client_id: null,
  client_slug: null,
  client_name: null,
  profile_kind: "full",
  profile_label: "Full",
  gis_only: false,
  public_dataset: null,
  enforcing: false,
  granted: [],
  not_granted: [],
  council_network: null,
  attribution: null,
  home_bbox: null,
  council_networks: [],
};

let profile: DataProfile = FULL_FALLBACK;
let loaded = false;

/** A grant/dataset entry may be a bare name or an object with a name-like
 * field; keeps just a readable name either way. */
function entryName(entry: unknown): string | null {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") {
    const o = entry as Record<string, unknown>;
    for (const key of ["label", "name", "dataset", "dataset_key", "key", "id"]) {
      if (typeof o[key] === "string") return o[key] as string;
    }
  }
  return null;
}

function names(list: unknown[] | undefined): string[] {
  return (list ?? []).map(entryName).filter((n): n is string => !!n);
}

function isBbox(v: unknown): v is [number, number, number, number] {
  return Array.isArray(v) && v.length === 4 && v.every((x) => typeof x === "number");
}

/** Keeps only well-formed entries (an older backend sends none). */
function councilNetworks(list: unknown[] | undefined): CouncilNetwork[] {
  const out: CouncilNetwork[] = [];
  for (const e of list ?? []) {
    if (!e || typeof e !== "object") continue;
    const o = e as Record<string, unknown>;
    if (typeof o.council !== "string" || !o.council) continue;
    out.push({ council: o.council, name: typeof o.name === "string" && o.name ? o.name : o.council,
      attribution: typeof o.attribution === "string" && o.attribution ? o.attribution : null,
      bbox: isBbox(o.bbox) ? o.bbox : null });
  }
  return out;
}

/** Fetches the profile once. Call after login, before any feature init. */
export async function loadDataProfile(): Promise<DataProfile> {
  try {
    // retried (2026-10-08): a request lost in a network change must not
    // silently turn a GIS-only organisation into "full" for this session
    const me = await withRetry(() => getJson<DataAccessMeResponse>("/api/data-access/me"), { attempts: 4, baseMs: 500 });
    const gisOnly = me.gis_only === true;
    const kind: ProfileKind =
      me.profile_kind === "public" || me.profile_kind === "partial" || me.profile_kind === "full"
        ? me.profile_kind
        : gisOnly ? "public" : "full";
    profile = {
      client_id: me.client_id ?? null,
      client_slug: me.client_slug ?? null,
      client_name: me.client_name ?? null,
      profile_kind: kind,
      profile_label:
        me.profile_label ||
        (kind === "full" ? (me.client_name ? `Full (${me.client_name})` : "Full") : "Public GIS view"),
      gis_only: gisOnly,
      public_dataset: gisOnly ? (me.public_dataset || DEFAULT_PUBLIC_DATASET) : null,
      enforcing: me.enforcing === true,
      granted: names(me.granted),
      not_granted: names(me.not_granted),
      council_network: me.council_network ?? null,
      attribution: typeof me.attribution === "string" && me.attribution ? me.attribution : null,
      home_bbox:
        Array.isArray(me.home_bbox) && me.home_bbox.length === 4 && me.home_bbox.every((v) => typeof v === "number")
          ? (me.home_bbox as [number, number, number, number])
          : null,
      council_networks: councilNetworks(me.council_networks),
    };
  } catch (err) {
    console.warn("Data profile unavailable; assuming full access:", err);
    profile = FULL_FALLBACK;
  }
  loaded = true;
  installNotAvailableRejectionGuard();
  return profile;
}

export function getDataProfile(): DataProfile {
  return profile;
}

/** True only when the backend said so. Every GIS-only gate checks this. */
export function isGisOnly(): boolean {
  return loaded && profile.gis_only;
}

/** True when a 404 from an enforced route should read as "not in your data
 * profile" rather than a genuine missing record. Never for a full profile,
 * so client 1's messages are unchanged. */
function restrictedProfile(): boolean {
  return loaded && (profile.gis_only || profile.profile_kind !== "full");
}

/** The caption to show when a panel's fetch failed: the not-available
 * sentence for an enforced 404 on a restricted profile, else `fallback`. */
export function loadErrorText(err: unknown, fallback: string): string {
  return err instanceof NotAvailableError && restrictedProfile() ? NOT_AVAILABLE_TEXT : fallback;
}

let guardInstalled = false;

/** A 404 from an enforced route that some fire-and-forget caller didn't
 * catch should not surface as an uncaught error on a restricted profile. */
function installNotAvailableRejectionGuard(): void {
  if (guardInstalled) return;
  guardInstalled = true;
  window.addEventListener("unhandledrejection", (event) => {
    if (event.reason instanceof NotAvailableError && restrictedProfile()) {
      event.preventDefault();
      console.info(`${event.reason.url}: ${NOT_AVAILABLE_TEXT}`);
    }
  });
}

/** Adds the "Data profile: …" badge at the top of the sidebar, next to
 * the workspace switcher. */
export function renderDataProfileBadge(): void {
  const sidebar = document.getElementById("sidebar");
  if (!sidebar) return;
  document.getElementById("data-profile-badge")?.remove();
  const badge = document.createElement("div");
  badge.id = "data-profile-badge";
  badge.className = `data-profile-badge data-profile-${profile.profile_kind}`;
  badge.textContent = `Data profile: ${profile.profile_label}`;
  const missing = profile.not_granted;
  const lines = [profile.client_name ? `Organisation: ${profile.client_name}` : null];
  if (missing.length === 0) {
    lines.push("All datasets granted.");
  } else {
    lines.push(`${missing.length} dataset(s) not granted:`);
    lines.push(...missing.map((n) => `  • ${n}`));
  }
  if (profile.gis_only) lines.push("GIS-only view: the map shows public layers only.");
  badge.title = lines.filter((l): l is string => !!l).join("\n");
  const switcher = document.getElementById("workspace-switcher");
  if (switcher) switcher.after(badge);
  else sidebar.prepend(badge);
}

/** Field order and labels for the GIS-only pipe info panel. */
const PUBLIC_FIELDS: [string, string][] = [
  ["council", "Council"],
  ["material", "Material"],
  ["diameter_mm", "Diameter (mm)"],
  ["install_year", "Install year"],
];

/** The public attributes of one pipe, straight from the /api/pipes payload
 * already on the map (no extra request). Null if it isn't loaded. */
export function publicPipeRow(assetId: string): [string, string][] | null {
  const { pipes, geometry } = getState();
  if (!pipes) return null;
  const idx = pipes.columns.asset_id.indexOf(assetId);
  if (idx === -1) return null;
  const cols = pipes.columns as unknown as Record<string, unknown[] | undefined>;
  let waterType: string | null = null;
  if (Array.isArray(cols.water_type)) waterType = String(cols.water_type[idx] ?? "");
  else if (pipes.color_col === "water_type") waterType = pipes.columns._value[idx];
  else if (geometry) {
    const g = geometry.asset_id.indexOf(assetId);
    if (g !== -1) waterType = geometry.water_type[g];
  }
  const rows: [string, string][] = [
    ["Asset ID", assetId],
    ["Water type", waterType || "—"],
  ];
  for (const [field, label] of PUBLIC_FIELDS) {
    const series = cols[field];
    if (!series) continue;
    const v = series[idx];
    rows.push([label, v === null || v === undefined || v === "" ? "—" : String(v)]);
  }
  return rows;
}
